import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8 md:p-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: December 12, 2025</p>

        <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="leading-relaxed">
              We collect information you provide directly to us when you create an account, use our services, or communicate with us. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account information (name, email, phone number, organization details)</li>
              <li>Booking information (dates, times, services, customer details)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Device information (IP address, browser type, device fingerprints for waiver verification)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our booking platform</li>
              <li>Process bookings and payments</li>
              <li>Send booking confirmations, reminders, and updates</li>
              <li>Provide customer support</li>
              <li>Verify digital waiver signatures with device fingerprinting</li>
              <li>Prevent fraud and ensure platform security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Data Sharing and Storage</h2>
            <p className="leading-relaxed">
              <strong>Your data is yours.</strong> We do not sell your customer data to third parties. Each organization gets its own dedicated database. We only share data with:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service providers (Stripe for payments, Twilio for SMS, email providers)</li>
              <li>As required by law or legal process</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Data Security</h2>
            <p className="leading-relaxed">
              We use industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data at rest and in transit</li>
              <li>Secure authentication with JWT tokens</li>
              <li>Regular security audits and monitoring</li>
              <li>Isolated databases for each organization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Waiver Verification Data</h2>
            <p className="leading-relaxed">
              When customers sign digital waivers, we collect device fingerprints, IP addresses, browser information, and signature data. This information is stored securely and used exclusively for legal verification purposes. Access to this sensitive data is logged in our activity log.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Your Rights</h2>
            <p className="leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="leading-relaxed mb-4">
              We use cookies and similar tracking technologies to improve your experience, understand usage patterns, and deliver relevant content. Here&apos;s what you need to know:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Types of Cookies We Use</h3>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <strong>Essential Cookies:</strong> Required for basic site functionality like authentication, security, and session management. These cannot be disabled.
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (Google Analytics). These are only loaded with your consent.
              </li>
              <li>
                <strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness. These are only loaded with your consent.
              </li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Managing Your Cookie Preferences</h3>
            <p className="leading-relaxed mb-4">
              When you first visit our site, you&apos;ll see a cookie consent banner where you can:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Accept all cookies</li>
              <li>Decline non-essential cookies</li>
              <li>Customize your preferences by category</li>
            </ul>
            <p className="leading-relaxed mt-4">
              You can change your preferences at any time by clearing your browser&apos;s localStorage and refreshing the page, or through your browser&apos;s cookie settings.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Third-Party Services</h3>
            <p className="leading-relaxed">
              We may use third-party analytics and advertising services that place their own cookies. These services have their own privacy policies:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Google Analytics - <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              <li>Stripe (payments) - <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. GDPR Compliance (European Users)</h2>
            <p className="leading-relaxed mb-4">
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Access:</strong> Request a copy of all personal data we hold about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate personal data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data (&quot;right to be forgotten&quot;)</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Request your data in a machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="leading-relaxed mt-4">
              <strong>Legal Basis for Processing:</strong> We process your data based on: (a) your consent, (b) performance of a contract, (c) compliance with legal obligations, or (d) our legitimate business interests.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. CCPA Compliance (California Residents)</h2>
            <p className="leading-relaxed mb-4">
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Know:</strong> You can request information about what personal data we collect, use, and disclose</li>
              <li><strong>Right to Delete:</strong> You can request deletion of your personal data, subject to certain exceptions</li>
              <li><strong>Right to Opt-Out:</strong> You can opt-out of the sale of your personal information</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
            </ul>
            <p className="leading-relaxed mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <strong>We Do Not Sell Your Personal Information.</strong> BookingFlows does not sell, rent, or trade your personal information to third parties for monetary or other valuable consideration.
            </p>
            <p className="leading-relaxed mt-4">
              To exercise your CCPA rights, please contact us at the email below. We will respond to verifiable requests within 45 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Data Retention</h2>
            <p className="leading-relaxed">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Active account data: Retained while your account is active</li>
              <li>Booking records: Retained for 7 years for legal and tax compliance</li>
              <li>Waiver signatures: Retained for the statute of limitations period in your jurisdiction</li>
              <li>Analytics data: Anonymized after 26 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="font-semibold">support@bookingflows.zendesk.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
